'use client';

import { c as _c } from "react/compiler-runtime";
import dynamic from 'next/dynamic';
import { useTheme } from 'next-themes';
import { useState, useEffect, useRef } from 'react';
import { useIsClientMounted } from '@/hooks/useIsClientMounted';
import AppDock from '@/components/AppDock';
import SideTimeline from '@/components/SideTimeline';
import { timelineData } from '@/data/steveJobsTimeline';
import type { TimelineItem, ScrollMethods } from '@/types/timeline';
function AppleLoadingAnimation() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg className="absolute inset-0 size-full text-zinc-200 dark:text-zinc-800" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 22C7.79 22.05 6.8 20.68 5.96 19.47C4.25 17 2.94 12.45 4.7 9.39C5.57 7.87 7.13 6.91 8.82 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="absolute inset-0 flex items-center justify-center"><div className="relative size-12">{t0}<svg className="absolute inset-0 size-full animate-[appleFill_900ms_ease-in-out_infinite] text-zinc-900 dark:text-white" viewBox="0 0 24 24" fill="currentColor" style={{
          clipPath: "inset(100% 0 0 0)"
        }}><path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 22C7.79 22.05 6.8 20.68 5.96 19.47C4.25 17 2.94 12.45 4.7 9.39C5.57 7.87 7.13 6.91 8.82 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z" /></svg><style>{"\n          @keyframes appleFill {\n            0% { clip-path: inset(100% 0 0 0); }\n            50% { clip-path: inset(0 0 0 0); }\n            100% { clip-path: inset(100% 0 0 0); }\n          }\n        "}</style></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
const Timeline = dynamic(() => import('@/components/SteveTimeline'), {
  ssr: false,
  loading: () => <AppleLoadingAnimation />
});
export default function StevePage() {
  const $ = _c(15);
  const [activeIndex, setActiveIndex] = useState(0);
  const activeIndexRef = useRef(0);
  const timelineRef = useRef(null);
  const {
    resolvedTheme
  } = useTheme();
  const mounted = useIsClientMounted();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const handleKeyDown = e => {
        if (!timelineRef.current?.scrollTo) {
          return;
        }
        const currentIndex = activeIndexRef.current;
        const maxIndex = timelineData.length - 1;
        let targetIndex = null;
        bb10: switch (e.key) {
          case "ArrowDown":
          case "ArrowRight":
            {
              e.preventDefault();
              targetIndex = Math.min(currentIndex + 1, maxIndex);
              break bb10;
            }
          case "ArrowUp":
          case "ArrowLeft":
            {
              e.preventDefault();
              targetIndex = Math.max(currentIndex - 1, 0);
              break bb10;
            }
          case "Home":
            {
              e.preventDefault();
              targetIndex = 0;
              break bb10;
            }
          case "End":
            {
              e.preventDefault();
              targetIndex = maxIndex;
            }
        }
        if (targetIndex !== null) {
          timelineRef.current.scrollTo(targetIndex);
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = item => {
      const itemIndex = timelineData.findIndex(i => i.id === item.id);
      if (itemIndex !== -1 && timelineRef.current) {
        timelineRef.current.scrollTo(itemIndex);
      }
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleItemSelect = t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = index => {
      activeIndexRef.current = index;
      setActiveIndex(index);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleActiveIndexChange = t3;
  if (!mounted) {
    let t4;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="relative h-screen w-full overflow-hidden bg-white dark:bg-neutral-950"><AppDock /></div>;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    return t4;
  }
  const isDarkMode = resolvedTheme === "dark";
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      touchAction: "pan-x",
      overscrollBehavior: "none"
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== isDarkMode) {
    t5 = <Timeline ref={timelineRef} onActiveIndexChange={handleActiveIndexChange} isDarkMode={isDarkMode} />;
    $[6] = isDarkMode;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== activeIndex || $[9] !== isDarkMode) {
    t6 = <SideTimeline onItemSelect={handleItemSelect} activeIndex={activeIndex} isDarkMode={isDarkMode} />;
    $[8] = activeIndex;
    $[9] = isDarkMode;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <AppDock />;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t5 || $[13] !== t6) {
    t8 = <div className="relative h-screen w-full overflow-hidden bg-white dark:bg-neutral-950" style={t4}>{t5}{t6}{t7}</div>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
}